import React, { CSSProperties, useEffect, useState } from 'react'
import { List, Modal, Row, Timeline, Typography } from 'antd'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { Request, RequestItinerary } from 'src/types'

import { ButtonTitled, Empty } from 'src/components/Ant'
import { saveRequestItinerary } from 'src/core/store/ducks/requests/actions'

import { Card } from './styles'
import ModalForm from '../ModalForm'

interface Props {
  bodyStyle?: CSSProperties
  enableModal?: boolean
  handleDelete?(item: RequestItinerary): void
  handleEdit?(item: RequestItinerary): void
  request: Request
}

function ItinerariesSummary({
  bodyStyle = {},
  enableModal = false,
  handleDelete,
  handleEdit,
  request
}: Props): JSX.Element {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [itinerariesList, setItinerariesList] = useState<RequestItinerary[]>([])
  const [visibleModal, setVisibleModal] = useState<boolean>(false)
  const [itemEdit, setItemEdit] = useState<RequestItinerary>()

  useEffect(() => {
    setItinerariesList(
      request.request_itineraries.filter(
        (item: RequestItinerary) => item.activity
      )
    )
  }, [request.request_itineraries])

  function openEditModal(item: RequestItinerary) {
    setVisibleModal(true)
    setItemEdit(item)
  }

  function confirmDelete(item: RequestItinerary) {
    Modal.confirm({
      cancelText: t('common.actions.cancel'),
      content: t('common.actions.delete.message', {
        number: `#${item.request_itinerary_number}`
      }),
      onCancel() {},
      onOk: async () => {
        return new Promise(() => {
          dispatch(saveRequestItinerary({ ...item, active: false }))
        })
      },
      title: t('common.actions.delete.title')
    })
  }

  return (
    <>
      {enableModal && (
        <ModalForm
          itemEdit={itemEdit}
          request={request}
          toggle={() => {
            setVisibleModal(false)
            setItemEdit(null)
          }}
          visible={visibleModal}
        />
      )}
      <Card
        data-test-id="summary-itinerary"
        bodyStyle={bodyStyle}
        extra={[
          ...(enableModal
            ? [
                <ButtonTitled
                  data-test-id="summary-itinerary-add"
                  icon="plus"
                  key="add-itinerary"
                  onClick={() => setVisibleModal(true)}
                  size="default"
                  tooltip="common.actions.add"
                />
              ]
            : [])
        ]}
        title="web.itinerary.list-title"
      >
        {itinerariesList.length ? (
          <Timeline>
            {itinerariesList.map((item: RequestItinerary) => (
              <Timeline.Item key={item.request_itinerary_id || item.tmp_id}>
                <ul className="ant-list-items">
                  <List.Item
                    actions={[
                      ...(!!handleEdit || enableModal
                        ? [
                            <ButtonTitled
                              icon="edit"
                              onClick={
                                enableModal
                                  ? () => openEditModal(item)
                                  : () => handleEdit(item)
                              }
                              shape="circle"
                              tooltip="common.actions.edit"
                            />
                          ]
                        : []),
                      ...(!!handleDelete || enableModal
                        ? [
                            <ButtonTitled
                              icon="delete"
                              onClick={
                                enableModal
                                  ? () => confirmDelete(item)
                                  : () => handleDelete(item)
                              }
                              shape="circle"
                              tooltip="common.actions.remove"
                              type="danger"
                            />
                          ]
                        : [])
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        item.request_itinerary_id
                          ? `#${item.request_itinerary_number}`
                          : undefined
                      }
                      description={
                        <>
                          <Row>
                            <Typography.Text strong>
                              {`${t('web.itinerary.form.activity.title')}: `}
                            </Typography.Text>
                            {item.activity}
                          </Row>
                          <Row>
                            <Typography.Text strong>
                              {`${t('web.itinerary.form.place.title')}: `}
                            </Typography.Text>
                            {item.description}
                          </Row>
                          <Row>
                            <Typography.Text strong>
                              {`${t('web.itinerary.form.reference.title')}: `}
                            </Typography.Text>
                            {item.reference.name}
                          </Row>
                          <Row>
                            <Typography.Text strong>
                              {`${t('web.itinerary.form.start')}: `}
                            </Typography.Text>
                            {moment
                              .unix(item.started_on)
                              .utc()
                              .format('DD/MM/YYYY HH:mm')}
                          </Row>
                          <Row>
                            <Typography.Text strong>
                              {`${t('web.itinerary.form.end')}: `}
                            </Typography.Text>
                            {moment
                              .unix(item.ended_on)
                              .utc()
                              .format('DD/MM/YYYY HH:mm')}
                          </Row>
                        </>
                      }
                    />
                  </List.Item>
                </ul>
              </Timeline.Item>
            ))}
          </Timeline>
        ) : (
          <Empty />
        )}
      </Card>
    </>
  )
}

export default ItinerariesSummary
